import { Duration, Instant, LocalDateTime, ZoneId } from 'js-joda';
import { cloneDeep, isNull } from 'es-toolkit/compat';

import { core, toJSON, toJSONArray } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';

interface State {
  currentLock?: core.TicketGroup;
  packageLock: {
    ticketTypePackageId: number;
    size: number;
    locks: { [id: number]: core.LockedTicketSubset };
  } | null;
  lockedUntilMs: number | null;
  locks: core.LockedTickets[] | null;
  requestInProgress: boolean;
  context: {
    dateTimeString?: string;
  };
  // Used for multi-checkout
  remainingLocks?: core.TicketGroup[];
  completedMultiCheckoutPurchaseIds?: number[];
}

export const initialState: State = {
  currentLock: undefined,
  remainingLocks: [],
  completedMultiCheckoutPurchaseIds: [],
  packageLock: null,
  lockedUntilMs: null,
  locks: null,
  requestInProgress: false,
  context: {},
};

const initializedState = cloneDeep(initialState);

export default function reducer(state: State = initializedState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.LOCK_FETCH_ALL_DONE: {
      return {
        ...state,
        locks: toJSON(payload),
        requestInProgress: false,
      };
    }
    case AppConstants.PURCHASE_PACKAGE_DONE:
    case AppConstants.LOCK_CLEAR_PACKAGE: {
      return {
        ...state,
        packageLock: null,
        lockedUntilMs: state.packageLock ? null : state.lockedUntilMs,
        requestInProgress: false,
      };
    }
    case AppConstants.PURCHASE_DONE: {
      return {
        ...state,
        currentLock: undefined,
        packageLock: null,
        lockedUntilMs: null,
        requestInProgress: false,
      };
    }
    case AppConstants.LOCK_CLEAR: {
      return {
        ...state,
        currentLock: undefined,
        packageLock: null,
        lockedUntilMs: null,
        requestInProgress: false,
        remainingLocks: [],
        completedMultiCheckoutPurchaseIds: [],
      };
    }
    case AppConstants.LOCK_TICKETS_DONE: {
      const { businessTimeZone } = context;
      const currentLock = toJSON<core.TicketGroup>(payload);
      const lockSubset = (currentLock as core.TicketGroup).ticketSubset?.[0];
      const lockedUntilDatetime = lockSubset.lockedUntilDatetime;

      const lockedUntilMs = getLockedUntilMs(lockedUntilDatetime, businessTimeZone);

      return {
        ...state,
        currentLock,
        lockedUntilMs,
        context: {
          dateTimeString: undefined,
        },
        requestInProgress: false,
        completedMultiCheckoutPurchaseIds: [], // Clear any remaining multi-checkout purchases when locking a new set of tickets m
      };
    }
    case AppConstants.LOCK_PACKAGE_DONE: {
      const { request } = context;
      const {
        ticketTypePackageId,
        lockTicketsRequest: { ticketTypeId, size },
      } = request;
      let { packageLock } = state;
      if (
        isNull(packageLock) ||
        packageLock.ticketTypePackageId !== ticketTypePackageId ||
        packageLock.size !== size
      ) {
        packageLock = {
          ticketTypePackageId,
          size,
          locks: {},
        };
      }
      packageLock.locks[ticketTypeId] = toJSON<core.LockResponse>(payload)
        .lockedTicketSubset as core.LockedTicketSubset;

      let lockedUntilMs: number | null = null;
      if (context.lockDurationMinutes) {
        lockedUntilMs = Instant.now()
          .plus(Duration.ofMinutes(context.lockDurationMinutes))
          .toEpochMilli();
      }
      return {
        ...state,
        packageLock,
        lockedUntilMs,
        requestInProgress: false,
      };
    }
    case AppConstants.FETCH_BUSINESS_LOCK_DONE: {
      const { businessTimeZone } = context;

      const currentLock = toJSON<core.TicketGroup>(payload);

      const lockSubset = (currentLock as core.TicketGroup).ticketSubset?.[0];
      const lockedUntilDatetime = lockSubset.lockedUntilDatetime;
      const lockedUntilMs = getLockedUntilMs(lockedUntilDatetime, businessTimeZone);

      return {
        ...state,
        currentLock,
        lockedUntilMs,
        context: {
          dateTimeString: lockSubset.requestedDateTime || undefined,
        },
      };
    }
    case AppConstants.FETCH_BUSINESS_LOCK_STARTING:
    case AppConstants.LOCK_FETCH_ALL_STARTING:
    case AppConstants.LOCK_PACKAGE_STARTING:
      return {
        ...state,
        requestInProgress: true,
      };
    case AppConstants.LOCK_TICKETS_STARTING: {
      return {
        ...state,
        context,
        requestInProgress: true,
      };
    }
    case AppConstants.FETCH_BUSINESS_LOCK_FAILED:
    case AppConstants.LOCK_FETCH_ALL_FAILED:
    case AppConstants.LOCK_PACKAGE_FAILED:
    case AppConstants.LOCK_TICKETS_FAILED:
      return {
        ...state,
        requestInProgress: false,
      };
    case AppConstants.LOCK_SELECT_NEXT_IN_SEQUENCE: {
      if (!!state.currentLock || (state.remainingLocks?.length ?? 0) < 1) {
        return state;
      }

      const { businessTimeZone } = context;
      const newCurrentLock = state.remainingLocks?.[0];
      const lockSubset = newCurrentLock?.ticketSubset?.[0];
      const lockedUntilDatetime = lockSubset?.lockedUntilDatetime;

      const lockedUntilMs = getLockedUntilMs(lockedUntilDatetime, businessTimeZone);

      return {
        ...state,
        currentLock: newCurrentLock,
        lockedUntilMs,
        context: {
          dateTimeString: undefined,
        },
        requestInProgress: false,
        remainingLocks: state.remainingLocks?.slice(1) ?? [],
      };
    }
    case AppConstants.LOCK_FETCH_ALL_BY_IDS_DONE: {
      const ticketGroups = toJSONArray<core.TicketGroup>(payload);

      const sortedTicketGroups = ticketGroups.sort(sortLocksByRequestedStartTime);

      return {
        ...state,
        remainingLocks: sortedTicketGroups,
      };
    }
    case AppConstants.LOCK_ADD_COMPLETED_PURCHASE_ID: {
      const purchaseId = payload;
      return {
        ...state,
        completedMultiCheckoutPurchaseIds: [
          ...(state.completedMultiCheckoutPurchaseIds || []),
          purchaseId,
        ],
      };
    }
    default:
      return state;
  }

  function getLockedUntilMs(
    lockedUntilDatetime: string | null | undefined,
    businessTimeZone: string
  ) {
    return lockedUntilDatetime && businessTimeZone
      ? LocalDateTime.parse(lockedUntilDatetime)
          .atZone(ZoneId.of(businessTimeZone as string))
          .toInstant()
          .toEpochMilli()
      : context.lockDurationMinutes
        ? Instant.now().plus(Duration.ofMinutes(context.lockDurationMinutes)).toEpochMilli()
        : null;
  }

  function sortLocksByRequestedStartTime(
    lockedTicketGroupA: core.TicketGroup,
    lockedTicketGroupB: core.TicketGroup
  ) {
    const dateA = getRequestedStartTimeFromSubset(lockedTicketGroupA);
    const dateB = getRequestedStartTimeFromSubset(lockedTicketGroupB);

    return dateA && dateB ? dateA.compareTo(dateB) : 0;
  }

  function getRequestedStartTimeFromSubset(lockedTickedGroup: core.TicketGroup) {
    return lockedTickedGroup.ticketSubset?.[0]?.requestedDateTime
      ? LocalDateTime.parse(lockedTickedGroup.ticketSubset[0].requestedDateTime)
      : null;
  }
}
